import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTranslation } from '../i18n/useTranslation';
import { PortalEnvironmentOut } from '../types/apiRequests';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { request_type: string; environment_url?: string; environment_name?: string; message?: string }) => Promise<void>;
  serverName: string;
  hasDefaultUrl?: boolean;
  environments?: PortalEnvironmentOut[];
}

function isValidHttpsUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function RequestAccessDialog({ open, onClose, onSubmit, serverName, hasDefaultUrl = false, environments = [] }: Props) {
  const t = useTranslation();
  const [environmentUrl, setEnvironmentUrl] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const handleUrlChange = (value: string) => {
    setEnvironmentUrl(value);
    if (value && !isValidHttpsUrl(value)) {
      setUrlError(t('requestAccess.urlError'));
    } else {
      setUrlError(null);
    }
  };

  const handleSubmit = async () => {
    if (environments.length > 0 && !selectedEnvironment) {
      return;
    }
    if (environmentUrl && !isValidHttpsUrl(environmentUrl)) {
      setUrlError(t('requestAccess.urlError'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        request_type: 'organization',
        environment_url: environmentUrl || undefined,
        environment_name: selectedEnvironment || undefined,
        message: message || undefined,
      });
      setSuccess(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEnvironmentUrl('');
    setSelectedEnvironment('');
    setMessage('');
    setError(null);
    setUrlError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('requestAccess.title')}</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {t('requestAccess.success', { serverName })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: 1 }}>
              {t('requestAccess.description')}
            </Typography>
            {environments.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('requestAccess.environmentLabel')}</InputLabel>
                <Select
                  value={selectedEnvironment}
                  label={t('requestAccess.environmentLabel')}
                  onChange={(e) => {
                    const envName = e.target.value;
                    setSelectedEnvironment(envName);
                    const env = environments.find((env) => env.name === envName);
                    if (env?.default_base_url) {
                      setEnvironmentUrl(env.default_base_url);
                      setUrlError(null);
                    }
                  }}
                >
                  {environments.map((env) => (
                    <MenuItem key={env.name} value={env.name}>
                      <Box>
                        <Typography sx={{ fontSize: '0.9rem' }}>{env.display_name}</Typography>
                        {env.description && (
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {env.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(!hasDefaultUrl || (environments.length > 0 && environments.some((e) => e.default_base_url))) && (
              <TextField
                fullWidth
                label={t('requestAccess.envUrlLabel')}
                placeholder={t('requestAccess.envUrlPlaceholder')}
                value={environmentUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                error={!!urlError}
                sx={{ mb: 2 }}
                helperText={urlError || t('requestAccess.envUrlHelper')}
              />
            )}
            <TextField
              fullWidth
              label={t('requestAccess.messageLabel')}
              placeholder={t('requestAccess.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={3}
              inputProps={{ maxLength: 1000 }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {success ? t('common.close') : t('common.cancel')}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {loading ? t('requestAccess.submittingButton') : t('requestAccess.submitButton')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
